import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    inject,
    OnInit,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    LoggingService,
    PageLayoutComponent,
    SnackbarService,
    StepperComponent,
    StepperStep,
    SuccessDialog,
    UserState,
    ParallelReferralDialogResult,
    ExternalReferralDialogResult
} from '@shs-digi-care-monorepo/frontend-lib';
import { Button } from '@shui/angular';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { select } from '@ngxs/store';
import {
    Country,
    EFileEntity,
    EMPTY_PATH_PREFIX,
    IIroNamRequest,
    IS3File,
    IUser
} from '@shs-digi-care-monorepo/interfaces';
import { catchError, EMPTY, finalize, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DealType } from './models/deal-type.model';
import { DealTypeForm } from './forms/deal-type/deal-type.component';
import { InstrumentOnlyForm } from './forms/instrument-only/instrument-only.component';
import { StandardDealForm } from './forms/standard-deal/standard-deal.component';
import { OrderDetailsForm } from './forms/order-details/order-details.component';
import { ContactInformationForm } from './forms/contact-information/contact-information.component';
import { createAdditionalContactFormGroup } from './models/contact-information.model';
import { OverviewForm, OverviewContactData, OverviewOrderDetailsData } from './forms/overview/overview.component';
import { OneDxCustomerInformation, OneDxMaterialItem } from '../shared/models/onedx.model';
import { RequestService } from '../shared/services/request.service';
import { CreateIroNamCustomerRequest } from '../shared/services/request.service';
import { AttachmentService } from '../shared/services/attachment.service';
import { TransitionService, TransitionReferral } from '../shared/services/transition.service';

@Component({
    selector: 'app-request',
    templateUrl: './request.component.html',
    imports: [
        PageLayoutComponent,
        TranslatePipe,
        StepperComponent,
        DealTypeForm,
        InstrumentOnlyForm,
        StandardDealForm,
        OrderDetailsForm,
        ContactInformationForm,
        OverviewForm,
        Button,
        RouterModule
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RequestComponent implements OnInit {
    private translate = inject(TranslateService);
    private requestService = inject(RequestService);
    private attachmentService = inject(AttachmentService);
    private transitionService = inject(TransitionService);
    private me: Signal<IUser | null> = select(UserState.me);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private dialog = inject(MatDialog);
    private snackbar = inject(SnackbarService);
    private logger = inject(LoggingService);

    protected dealTypeForm = viewChild<DealTypeForm>(DealTypeForm);
    protected instrumentOnlyForm = viewChild<InstrumentOnlyForm>(InstrumentOnlyForm);
    protected standardDealForm = viewChild<StandardDealForm>(StandardDealForm);
    protected orderDetailsForm = viewChild<OrderDetailsForm>(OrderDetailsForm);
    protected contactInformationForm = viewChild<ContactInformationForm>(ContactInformationForm);
    protected overviewForm = viewChild<OverviewForm>(OverviewForm);

    protected isLoading: WritableSignal<boolean> = signal(false);
    protected initialLoading = signal(false);
    protected showOverview = signal(false);
    protected requestId: string | null = null;
    protected serverFiles = signal<IS3File[]>([]);
    protected completedSteps = signal(new Set<number>());
    protected completedStepsArray = computed(() => Array.from(this.completedSteps()));
    protected maxReachedStepIndex = computed(() => {
        const steps = this.completedSteps();
        return steps.size > 0 ? Math.max(...steps) + 1 : 0;
    });

    protected dealType = signal<DealType | null>(null);
    private loadedCustomerInformation = signal<OneDxCustomerInformation | null>(null);
    private loadedMaterials = signal<OneDxMaterialItem[]>([]);
    private formPatchVersion = signal(0);

    protected materials = computed<OneDxMaterialItem[]>(() => {
        this.formPatchVersion();
        const type = this.dealType();
        if (type === DealType.INSTRUMENT_ONLY) {
            return this.instrumentOnlyForm()?.formGroup.controls.materials.value ?? this.loadedMaterials();
        } else if (type === DealType.STANDARD_DEAL) {
            return this.standardDealForm()?.formGroup.controls.materials.value ?? this.loadedMaterials();
        }
        return this.loadedMaterials();
    });

    protected DealType = DealType;

    protected customerInformation = computed<OneDxCustomerInformation | null>(() => {
        const type = this.dealType();
        if (type === DealType.INSTRUMENT_ONLY) {
            return this.instrumentOnlyForm()?.customerInformation?.() ?? this.loadedCustomerInformation();
        } else if (type === DealType.STANDARD_DEAL) {
            return this.standardDealForm()?.customerInformation?.() ?? this.loadedCustomerInformation();
        }
        return this.loadedCustomerInformation();
    });

    protected overviewProductLine = computed<string | null>(() => {
        this.formPatchVersion();
        const type = this.dealType();
        if (type === DealType.INSTRUMENT_ONLY) {
            return this.instrumentOnlyForm()?.formGroup.controls.productLine.value ?? null;
        }
        return null;
    });

    protected soldToNumber = computed<string | null>(() => {
        this.formPatchVersion();
        if (this.dealType() !== DealType.INSTRUMENT_ONLY) {
            return null;
        }

        return this.instrumentOnlyForm()?.formGroup.controls.soldTo.value ?? null;
    });

    protected shipToNumber = computed<string | null>(() => {
        this.formPatchVersion();
        if (this.dealType() !== DealType.INSTRUMENT_ONLY) {
            return null;
        }

        return this.instrumentOnlyForm()?.formGroup.controls.shipTo.value ?? null;
    });

    protected contactData = computed<OverviewContactData | null>(() => {
        this.formPatchVersion();
        const form = this.contactInformationForm();
        if (!form) return null;
        const fg = form.formGroup;
        return {
            customerEmail: fg.controls.customerEmail.value,
            tasEmail: fg.controls.tasEmail.value,
            salesRepEmail: fg.controls.salesRepEmail.value,
            pmInstallationEmail: fg.controls.pmInstallationEmail.value,
            additionalContacts: fg.controls.additionalContacts.controls
                .filter((c) => c.controls.title.value || c.controls.email.value)
                .map((c) => ({
                    title: c.controls.title.value,
                    email: c.controls.email.value
                }))
        };
    });

    protected orderDetailsData = computed<OverviewOrderDetailsData | null>(() => {
        this.formPatchVersion();
        const form = this.orderDetailsForm();
        if (!form) return null;
        const fg = form.formGroup;
        return {
            isDistributor: fg.controls.isDistributor.value,
            distributorName: fg.controls.distributorName.value,
            isCprContract: fg.controls.isCprContract.value,
            monthsOfInventory: fg.controls.monthsOfInventory.value,
            additionalEmails: fg.controls.additionalEmails.value
        };
    });

    protected steps: StepperStep[] = [
        {
            title: this.translate.instant('request.steps.deal-type.title'),
            description: this.translate.instant('request.steps.deal-type.description'),
            disabled: false
        },
        {
            title: this.translate.instant('request.steps.deal-information.title'),
            description: this.translate.instant('request.steps.deal-information.description'),
            disabled: false
        },
        {
            title: this.translate.instant('request.steps.order-details.title'),
            description: this.translate.instant('request.steps.order-details.description'),
            disabled: false
        },
        {
            title: this.translate.instant('request.steps.contact-information.title'),
            description: this.translate.instant('request.steps.contact-information.description'),
            disabled: false
        },
        {
            title: this.translate.instant('request.steps.overview.title'),
            description: this.translate.instant('request.steps.overview.description'),
            disabled: false
        }
    ];

    public ngOnInit(): void {
        this.requestId = this.route.snapshot.queryParamMap.get('requestId');
        this.logger.info('Request page initialized', {
            requestId: this.requestId,
            mode: this.requestId ? 'edit' : 'new'
        });
        if (this.requestId) {
            this.initialLoading.set(true);
            this.loadRequest(this.requestId);
        }
    }

    protected onContinue(stepIndex: number): void {
        if (stepIndex === 0) {
            const newDealType = this.dealTypeForm()?.formGroup.controls.dealType.value ?? null;
            if (newDealType !== this.dealType() && this.completedSteps().size > 0) {
                this.completedSteps.set(new Set());
                this.showOverview.set(false);
            }
            this.dealType.set(newDealType);
        }

        this.completedSteps.update((set) => new Set(set).add(stepIndex));

        if (stepIndex === 3) {
            this.showOverview.set(true);
            this.scrollToStep(4);
            return;
        }

        this.scrollToStep(stepIndex + 1);
    }

    protected onEditFromOverview(stepIndex: number): void {
        this.completedSteps.update((set) => {
            const updated = new Set(set);
            for (let i = 0; i < stepIndex; i++) {
                updated.add(i);
            }
            return updated;
        });
        this.showOverview.set(false);
        this.scrollToStep(stepIndex);
    }

    private scrollToStep(stepIndex: number): void {
        if (stepIndex < 0) {
            return;
        }

        setTimeout(() => {
            const nextFormId = `form-step-${stepIndex}`;
            const nextFormElement = document.getElementById(nextFormId);
            if (nextFormElement) {
                const scrollContainer = nextFormElement.closest('.overflow-y-auto');
                if (scrollContainer) {
                    const elementTop = nextFormElement.getBoundingClientRect().top;
                    const containerTop = scrollContainer.getBoundingClientRect().top;
                    scrollContainer.scrollTo({
                        top: scrollContainer.scrollTop + (elementTop - containerTop),
                        behavior: 'smooth'
                    });
                }
            }
        }, 0);
    }

    protected isStepCompleted(stepIndex: number): boolean {
        return this.completedSteps().has(stepIndex);
    }

    protected isStepRendered(stepIndex: number): boolean {
        if (this.requestId) return true;
        if (stepIndex === 0) return true;
        return this.completedSteps().has(stepIndex - 1);
    }

    protected isStepHidden(stepIndex: number): boolean {
        if (this.showOverview()) {
            return stepIndex !== 4;
        }

        return stepIndex === 4;
    }

    protected onSave(): void {
        const user = this.me();
        if (!user) {
            this.logger.warn('Cannot save: current user not available');
            return;
        }

        const payload = this.buildPayload(user);
        const isNew = !this.requestId;
        this.logger.info(isNew ? 'Saving new IRO request (draft)' : 'Updating existing IRO request (draft)', {
            requestId: this.requestId,
            dealType: this.dealType()
        });
        this.isLoading.set(true);

        this.saveObservable(payload)
            .pipe(
                take(1),
                switchMap((response) => this.uploadPendingFiles(response.id).pipe(map(() => response))),
                tap((response) => {
                    this.requestId = response.id;
                    this.overviewForm()?.formGroup.controls.files.setValue([]);
                    this.logger.info('IRO request saved successfully', { id: response.id });
                    this.router.navigate([], {
                        queryParams: { requestId: response.id },
                        queryParamsHandling: 'merge'
                    });
                    this.snackbar.success('request.messages.save-success');
                    this.refreshServerFiles(response.id);
                }),
                catchError((error) => {
                    this.logger.error('Failed to save IRO request', { error: error?.message ?? error });
                    this.snackbar.error('request.messages.save-error');
                    throw error;
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    protected onSubmit(): void {
        const user = this.me();
        if (!user) {
            this.logger.warn('Cannot submit: current user not available');
            return;
        }

        const payload = this.buildPayload(user);
        const isNew = !this.requestId;
        this.logger.info(isNew ? 'Submitting new IRO request' : 'Submitting existing IRO request', {
            requestId: this.requestId,
            dealType: this.dealType()
        });
        this.isLoading.set(true);

        this.saveObservable(payload)
            .pipe(
                take(1),
                switchMap((saveResponse) => {
                    this.requestId = saveResponse.id;
                    return this.uploadPendingFiles(saveResponse.id).pipe(map(() => saveResponse));
                }),
                switchMap((saveResponse) => {
                    this.logger.info('Submitting saved IRO request', { id: saveResponse.id });
                    return this.requestService.submitRequest(saveResponse.id).pipe(map(() => saveResponse));
                }),
                tap((saveResponse) => {
                    this.logger.info('IRO request submitted successfully', {
                        id: saveResponse.id,
                        displayId: saveResponse.displayId
                    });
                    this.openSuccessDialog(Number(saveResponse.displayId));
                }),
                catchError((error) => {
                    this.logger.error('Failed to submit IRO request', { error: error?.message ?? error });
                    this.snackbar.error('request.messages.submit-error');
                    throw error;
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    protected onDelete(): void {
        if (!this.requestId) {
            this.logger.warn('Cannot delete: no requestId set');
            return;
        }

        this.logger.info('Deleting IRO request', { requestId: this.requestId });
        this.isLoading.set(true);
        this.requestService
            .deleteRequest(this.requestId)
            .pipe(
                take(1),
                tap(() => {
                    this.logger.info('IRO request deleted successfully', { requestId: this.requestId });
                    this.router.navigate(['/']);
                }),
                catchError((error) => {
                    this.logger.error('Failed to delete IRO request', {
                        requestId: this.requestId,
                        error: error?.message ?? error
                    });
                    this.snackbar.error('request.messages.delete-error');
                    throw error;
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    protected onParallelReferralSubmit(entries: ParallelReferralDialogResult): void {
        const referrals: TransitionReferral[] = entries.map((entry) => ({
            name: entry.name,
            description: entry.description ?? undefined,
            referredOutTeam: entry.assignee?.type === 'team' ? entry.assignee.id : undefined,
            referredOutEmail: entry.assignee?.type === 'user' ? entry.assignee.email : undefined,
            isInternalReferredOut: true,
            markCommentInternal: entry.markAsInternal
        }));
        this.submitWithReferrals(referrals);
    }

    protected onExternalReferralSubmit(result: ExternalReferralDialogResult): void {
        const referrals: TransitionReferral[] = [
            {
                name: result.name,
                description: result.description ?? undefined,
                referredOutEmail: result.email,
                isInternalReferredOut: false,
                markCommentInternal: result.markAsInternal
            }
        ];
        this.submitWithReferrals(referrals);
    }

    private submitWithReferrals(referrals: TransitionReferral[]): void {
        const user = this.me();
        if (!user) {
            this.logger.warn('Cannot submit with referrals: current user not available');
            return;
        }

        const payload = this.buildPayload(user);
        this.logger.info('Submitting IRO request with referrals', {
            requestId: this.requestId,
            referralCount: referrals.length
        });
        this.isLoading.set(true);

        this.saveObservable(payload)
            .pipe(
                take(1),
                switchMap((saveResponse) => {
                    this.requestId = saveResponse.id;
                    return this.uploadPendingFiles(saveResponse.id).pipe(map(() => saveResponse));
                }),
                switchMap((saveResponse) => {
                    this.logger.info('Submitting saved IRO request', { id: saveResponse.id });
                    return this.requestService.submitRequest(saveResponse.id).pipe(map(() => saveResponse));
                }),
                switchMap((saveResponse) => {
                    this.logger.info('Transitioning IRO request to REFERRED_OUT', { id: saveResponse.id });
                    return this.transitionService.referOut(saveResponse.id, referrals).pipe(map(() => saveResponse));
                }),
                tap((saveResponse) => {
                    this.logger.info('IRO request submitted with referrals successfully', {
                        id: saveResponse.id,
                        displayId: saveResponse.displayId
                    });
                    this.openSuccessDialog(Number(saveResponse.displayId));
                }),
                catchError((error) => {
                    this.logger.error('Failed to submit IRO request with referrals', {
                        error: error?.message ?? error
                    });
                    this.snackbar.error('request.messages.submit-error');
                    throw error;
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    private saveObservable(payload: CreateIroNamCustomerRequest): Observable<IIroNamRequest> {
        if (this.requestId) {
            return this.requestService.updateRequest(this.requestId, payload);
        }
        return this.requestService.createRequest(payload);
    }

    private loadRequest(requestId: string): void {
        this.logger.debug('Loading IRO request', { requestId });
        this.isLoading.set(true);
        forkJoin({
            request: this.requestService.getRequestById(requestId),
            files: this.attachmentService
                .listFiles({
                    fileEntity: EFileEntity.Request,
                    requestId,
                    prefix: EMPTY_PATH_PREFIX
                })
                .pipe(catchError(() => of([] as IS3File[])))
        })
            .pipe(
                take(1),
                tap(({ request, files }) => {
                    this.requestId = request.id;
                    this.serverFiles.set(files);
                    const dealType = request.isInstrumentOnlyDeal ? DealType.INSTRUMENT_ONLY : DealType.STANDARD_DEAL;
                    this.logger.debug('IRO request loaded', {
                        id: request.id,
                        displayId: request.displayId,
                        dealType,
                        materialCount: request.materialItems?.length ?? 0
                    });
                    this.dealType.set(dealType);

                    this.loadedCustomerInformation.set({
                        customerName: request.soldToName ?? '',
                        quoteContractNumber: '',
                        soldToAddress: request.soldToAddress ?? '',
                        shipToAddress: request.shipToAddress ?? ''
                    });

                    const materials: OneDxMaterialItem[] = (request.materialItems ?? []).map((item) => ({
                        productLine: item.productLine,
                        description: item.description,
                        smn: item.smn,
                        kitSizeDescription: item.kitSizeDescription ?? '',
                        kitSize: item.kitSize ?? '',
                        annualTestVolume: item.annualTestVolume,
                        inventoryAmt: item.inventoryAmt,
                        implementationQuantity: item.implementationQuantity,
                        orderQuantity: item.orderQuantity,
                        unitPrice: item.unitPrice,
                        totalPrice: item.totalPrice
                    }));
                    this.loadedMaterials.set(materials);

                    this.populateFormData(request, dealType, materials);
                }),
                catchError((error) => {
                    this.logger.error('Failed to load IRO request', {
                        requestId,
                        error: error?.message ?? error
                    });
                    this.snackbar.error('request.messages.load-error');
                    throw error;
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    private populateFormData(request: IIroNamRequest, dealType: DealType, materials: OneDxMaterialItem[]): void {
        setTimeout(() => {
            this.dealTypeForm()?.formGroup.patchValue({ dealType });

            const customerInfo: OneDxCustomerInformation = {
                customerName: request.soldToName ?? '',
                quoteContractNumber: '',
                soldToAddress: request.soldToAddress ?? '',
                shipToAddress: request.shipToAddress ?? ''
            };

            if (dealType === DealType.INSTRUMENT_ONLY) {
                const form = this.instrumentOnlyForm();
                form?.formGroup.patchValue({
                    productLine: request.productLine ?? null,
                    soldTo: request.soldTo ?? null,
                    shipTo: request.shipTo ?? null,
                    materials
                });
                form?.customerInformation.set(customerInfo);
                form?.rowData.set(materials);
            } else {
                const form = this.standardDealForm();
                form?.formGroup.patchValue({
                    materials
                });
                form?.customerInformation.set(customerInfo);
                form?.rowData.set(materials);
            }

            this.orderDetailsForm()?.formGroup.patchValue({
                isDistributor: request.isDistributor ?? null,
                distributorName: request.distributorName ?? null,
                isCprContract: request.isCprContract ?? null,
                monthsOfInventory: request.monthsOfInventory ?? null,
                additionalEmails: request.additionalEmails ?? null
            });

            const contactForm = this.contactInformationForm();
            if (contactForm) {
                contactForm.formGroup.patchValue({
                    customerEmail: request.customerEmail ?? null,
                    tasEmail: request.tasEmail ?? null,
                    salesRepEmail: request.salesRepEmail ?? null,
                    pmInstallationEmail: request.pmInstallationEmail ?? null
                });

                const contacts = contactForm.formGroup.controls.additionalContacts;
                contacts.clear();
                if (request.additionalContacts?.length) {
                    for (const contact of request.additionalContacts) {
                        const group = createAdditionalContactFormGroup();
                        group.patchValue({ title: contact.title, email: contact.email });
                        contacts.push(group);
                    }
                } else {
                    contacts.push(createAdditionalContactFormGroup());
                }
            }

            this.overviewForm()?.formGroup.patchValue({
                subject: request.subject ?? null,
                reason: request.reason ?? null,
                shipmentRequiredByDate: request.shipmentRequiredByDate
                    ? new Date(request.shipmentRequiredByDate).toISOString().split('T')[0]
                    : null,
                isUrgent: request.isUrgent ?? false,
                emailHidingEnabled: request.emailHidingEnabled ?? false,
                informationalOnly: request.informationalOnly ?? false
            });

            this.formPatchVersion.update((v) => v + 1);
            this.completedSteps.set(new Set([0, 1, 2, 3]));
            this.showOverview.set(true);
            this.initialLoading.set(false);
        }, 100);
    }

    private openSuccessDialog(displayId: number): void {
        const dialogRef = this.dialog.open(SuccessDialog, {
            data: { displayId },
            width: '500px'
        });

        dialogRef
            .backdropClick()
            .pipe(
                tap(() => {
                    dialogRef.close();
                    this.router.navigate(['/']);
                })
            )
            .subscribe();
    }

    private buildPayload(user: IUser): CreateIroNamCustomerRequest {
        const dealType = this.dealType();
        const isInstrumentOnly = dealType === DealType.INSTRUMENT_ONLY;
        const customerInfo = this.customerInformation();

        const instrumentOnly = this.instrumentOnlyForm();
        const orderDetails = this.orderDetailsForm();
        const contactInfo = this.contactInformationForm();
        const overview = this.overviewForm();

        return {
            phoneNumber: user.phoneNumber ?? undefined,
            country: user.country ?? Country.US,
            subject: overview?.formGroup.controls.subject.value ?? '',
            reason: overview?.formGroup.controls.reason.value ?? undefined,
            isInstrumentOnlyDeal: isInstrumentOnly,
            productLine: isInstrumentOnly
                ? (instrumentOnly?.formGroup.controls.productLine.value ?? undefined)
                : undefined,
            soldTo: isInstrumentOnly ? (instrumentOnly?.formGroup.controls.soldTo.value ?? undefined) : undefined,
            soldToAddress: customerInfo?.soldToAddress ?? undefined,
            soldToName: customerInfo?.customerName ?? undefined,
            shipTo: isInstrumentOnly ? (instrumentOnly?.formGroup.controls.shipTo.value ?? undefined) : undefined,
            shipToName: customerInfo?.customerName ?? undefined,
            shipToAddress: customerInfo?.shipToAddress ?? undefined,
            isDistributor: orderDetails?.formGroup.controls.isDistributor.value ?? false,
            distributorName: orderDetails?.formGroup.controls.distributorName.value ?? undefined,
            isCprContract: orderDetails?.formGroup.controls.isCprContract.value ?? false,
            monthsOfInventory: orderDetails?.formGroup.controls.monthsOfInventory.value
                ? Number(orderDetails.formGroup.controls.monthsOfInventory.value)
                : undefined,
            shipmentRequiredByDate: overview?.formGroup.controls.shipmentRequiredByDate.value
                ? new Date(overview.formGroup.controls.shipmentRequiredByDate.value)
                : undefined,
            customerEmail: contactInfo?.formGroup.controls.customerEmail.value ?? undefined,
            tasEmail: contactInfo?.formGroup.controls.tasEmail.value ?? undefined,
            salesRepEmail: contactInfo?.formGroup.controls.salesRepEmail.value ?? undefined,
            pmInstallationEmail: contactInfo?.formGroup.controls.pmInstallationEmail.value ?? undefined,
            additionalContacts: contactInfo?.formGroup.controls.additionalContacts.controls
                .filter((c) => c.controls.title.value && c.controls.email.value)
                .map((c) => ({
                    title: c.controls.title.value as string,
                    email: c.controls.email.value as string
                })),
            additionalEmails: orderDetails?.formGroup.controls.additionalEmails.value ?? undefined,
            isUrgent: overview?.formGroup.controls.isUrgent.value ?? false,
            emailHidingEnabled: overview?.formGroup.controls.emailHidingEnabled.value ?? undefined,
            informationalOnly: overview?.formGroup.controls.informationalOnly.value ?? undefined,
            materialItems: this.materials().map((m) => ({
                productLine: m.productLine,
                description: m.description,
                smn: m.smn,
                kitSizeDescription: m.kitSizeDescription,
                kitSize: m.kitSize,
                annualTestVolume: m.annualTestVolume,
                inventoryAmt: m.inventoryAmt,
                implementationQuantity: m.implementationQuantity,
                orderQuantity: m.orderQuantity,
                unitPrice: m.unitPrice,
                totalPrice: m.totalPrice
            }))
        };
    }

    protected onPricingCleared(): void {
        const type = this.dealType();
        const form =
            type === DealType.INSTRUMENT_ONLY
                ? this.instrumentOnlyForm()
                : type === DealType.STANDARD_DEAL
                  ? this.standardDealForm()
                  : null;

        if (form) {
            const materials = form.formGroup.controls.materials.value;
            if (materials) {
                const cleared = materials.map((m) => ({ ...m, unitPrice: 0, totalPrice: 0 }));
                form.formGroup.controls.materials.setValue(cleared);
            }
        }
    }

    protected onDownloadAttachment(fileKey: string): void {
        if (!this.requestId) return;
        this.attachmentService
            .downloadFile({ fileEntity: EFileEntity.Request, requestId: this.requestId, filePath: fileKey })
            .pipe(
                take(1),
                catchError((error) => {
                    this.logger.error('Failed to download attachment', { error: error?.message ?? error });
                    this.snackbar.error('request.messages.download-error');
                    return EMPTY;
                })
            )
            .subscribe();
    }

    protected onDeleteAttachment(fileKey: string): void {
        if (!this.requestId) return;
        this.attachmentService
            .deleteFile({ fileEntity: EFileEntity.Request, requestId: this.requestId, filePath: fileKey })
            .pipe(
                take(1),
                tap(() => this.serverFiles.update((files) => files.filter((f) => f.Key !== fileKey))),
                catchError((error) => {
                    this.logger.error('Failed to delete attachment', { error: error?.message ?? error });
                    this.snackbar.error('request.messages.delete-attachment-error');
                    return EMPTY;
                })
            )
            .subscribe();
    }

    private uploadPendingFiles(requestId: string): Observable<unknown> {
        const files = this.overviewForm()?.formGroup.controls.files.value ?? [];
        const pendingFiles = files.filter((f) => f.status === 'pending');
        if (pendingFiles.length === 0) return of(null);

        return forkJoin(
            pendingFiles.map((fileItem) =>
                this.attachmentService.uploadFile({
                    fileEntity: EFileEntity.Request,
                    requestId,
                    file: fileItem.file,
                    fileName: fileItem.file.name
                })
            )
        );
    }

    private refreshServerFiles(requestId: string): void {
        this.attachmentService
            .listFiles({
                fileEntity: EFileEntity.Request,
                requestId,
                prefix: EMPTY_PATH_PREFIX
            })
            .pipe(
                take(1),
                tap((files) => this.serverFiles.set(files)),
                catchError(() => of([]))
            )
            .subscribe();
    }
}
